import React, { ReactElement, useEffect, useState } from "react";
import { Validator } from "../../models/Validator";
import {
  Group,
  Input,
  Switch,
  Text,
  useCombobox,
  Combobox,
  ActionIcon,
  Flex,
} from "@mantine/core";
import styles from "./ValidatorsFilter.module.scss";
import { formatNumber, parseLocaleNumber } from "../../utils/formatter";
import { calculateDateDifference } from "../transactionDetailsTable/TransactionDetailsTable.component";
import { IconFilterCog } from "@tabler/icons-react";
import { ValidatorsFilterMobilePage } from "../../pages/validators/ValidatorsFilterMobilePage";

export interface SelectItem {
  value: number;
  label: string;
}

export const freeSpaceOptions: SelectItem[] = [
  {
    value: 50000,
    label: formatNumber(50000, false, 0),
  },
  {
    value: 100000,
    label: formatNumber(100000, false, 0),
  },
  {
    value: 500000,
    label: formatNumber(500000, false, 0),
  },
  {
    value: 1000000,
    label: formatNumber(1000000, false, 0),
  },
  {
    value: 10000000,
    label: formatNumber(10000000, false, 0),
  },
];

export function ValidatorsFilter({
  validators,
  setValidators,
}: {
  validators: Validator[] | null;
  setValidators: (validators: Validator[] | null) => void;
}): ReactElement | null {
  const [days, setDays] = useState<number>(14);
  const [freeSpace, setFreeSpace] = useState<number>(freeSpaceOptions[0].value);
  const [switchActive, setSwitchActive] = useState<boolean>(false);
  const combobox = useCombobox();

  const [mobileFilterVisible, setMobileFilterVisible] =
    useState<boolean>(false);

  const showMobileFilter = () => {
    setMobileFilterVisible(true);
  };

  const hideMobileFilter = () => {
    setMobileFilterVisible(false);
  };

  const options = freeSpaceOptions.map((item) => (
    <Combobox.Option value={item.label} key={item.label}>
      {item.label}
    </Combobox.Option>
  ));

  /**
   * callback for child component
   * @param validators list of validators
   * @param switchActive state of filter switch
   * @param days number of days filter
   * @param freeSpace amount of free space filter
   * @param confirmed flag to check if function is envoked inside parent or child component
   */
  function applyFilters(
    validators: Validator[] | null,
    switchActive: boolean,
    days: number,
    freeSpace: number,
    confirmed: boolean
  ): void {
    // set values from child component
    setDays(days);
    setFreeSpace(freeSpace);
    if (switchActive) {
      setValidators(filterValidators(days, freeSpace, validators));
    } else {
      setValidators(validators ?? null);
    }
    confirmed && hideMobileFilter();
  }

  useEffect(() => {
    applyFilters(validators, switchActive, days, freeSpace, false);
  }, [validators, days, freeSpace, switchActive]);

  function filterValidators(
    days: number,
    freeSpace: number,
    validators: Validator[] | null
  ): Validator[] | null {
    if (!validators) {
      return null;
    }

    return validators.filter((validator: Validator) => {
      const daysRemaining = Math.floor(
        calculateDateDifference(new Date(), new Date(validator.endTime)) /
          (1000 * 60 * 60 * 24)
      );
      return validator.freeSpace / 1e9 >= freeSpace && daysRemaining >= days;
    });
  }

  return (
    <div>
      <Group className={styles.container} mb={15}>
        {!mobileFilterVisible && (
          <Flex direction={"row"} align={"center"} gap={10}>
            <Switch
              className={styles.switch}
              label="Show only stakeable"
              labelPosition={"left"}
              checked={switchActive}
              mr={18}
              onChange={(event) => setSwitchActive(event.currentTarget.checked)}
              style={{
                color: "var(--quinary)",
              }}
            />
            <div className={styles.verticalLine}></div>
            <ActionIcon
              className={styles.filterIcon}
              color={switchActive ? "#D9390B" : "#757575"}
              disabled={!switchActive}
              opacity={0.54}
              variant={"subtle"}
              onClick={
                mobileFilterVisible ? hideMobileFilter : showMobileFilter
              }
            >
              <IconFilterCog />
            </ActionIcon>
          </Flex>
        )}
        {mobileFilterVisible && switchActive && (
          <div className={styles.mobileFilter}>
            <ValidatorsFilterMobilePage
              validators={validators}
              setValidators={setValidators}
              applyFilters={applyFilters}
              outerDays={days}
              outerFreeSpace={freeSpace}
              closeFilter={hideMobileFilter}
            />
          </div>
        )}
        <Group className={styles.filterDropdowns}>
          <Text className={styles.text}>Show those with at least</Text>
          <Input
            disabled={!switchActive}
            defaultValue={14}
            type={"number"}
            className={`${styles.number} ${styles.customInput} ${!switchActive ? styles.disabled : ""}`}
            onChange={(val) => {
              setDays(+val.target.value);
            }}
          />
        </Group>
        <Group className={styles.filterDropdowns}>
          <Text className={styles.text}>days remaining and</Text>
          <Combobox
            onOptionSubmit={(optionValue) => {
              const selection = freeSpaceOptions.find(
                (el) => el.label === optionValue
              );
              if (selection) {
                setFreeSpace(selection.value);
              }

              combobox.closeDropdown();
            }}
            store={combobox}
          >
            <Combobox.Target>
              <Input
                disabled={!switchActive}
                className={`${styles.select} ${styles.customInput} ${!switchActive ? styles.disabled : ""}`}
                type={"text"}
                value={"" + formatNumber(freeSpace, false, 0)}
                onChange={(event) => {
                  setFreeSpace(
                    event.currentTarget.value ||
                      event.currentTarget.value.toLowerCase().includes("na")
                      ? parseLocaleNumber(event.currentTarget.value)
                      : 0
                  );
                  combobox.openDropdown();
                  combobox.updateSelectedOptionIndex();
                }}
                onClick={() => combobox.openDropdown()}
                onFocus={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
              />
            </Combobox.Target>

            <Combobox.Dropdown>
              <Combobox.Options>
                {options.length === 0 ? (
                  <Combobox.Empty>Nothing found</Combobox.Empty>
                ) : (
                  options
                )}
              </Combobox.Options>
            </Combobox.Dropdown>
          </Combobox>
          <Text className={styles.text}>free space or more.</Text>
        </Group>
      </Group>
    </div>
  );
}
